import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, CheckCircle, AlertTriangle, Wifi, Shield, Lock, Clock, RefreshCw } from 'lucide-react';
import MainLayout from '../components/MainLayout';
import api from '../services/api';
import { brokerAPI, BrokerStatus, BrokerName, BrokerFeature, BrokerFeatureToggle, BrokerCapital, ALL_BROKER_FEATURES, FEATURE_LABELS } from '../services/brokerApi';

// One card per broker. DHAN's actual order-account credentials stay on this same screen now
// (moved from Settings); Groww, Angel One, FYERS and Alice Blue each have their own real
// credential card with the same 6-feature toggle grid. The field count varies a lot per
// broker's own auth model — Alice Blue/Upstox/Zerodha/FYERS each need just 3 (App ID/Key,
// Secret, Redirect URI — OAuth-style app models requiring a real interactive browser login) —
// each card only asks for what that broker's login flow actually requires.
// `renewal` tells the user, in plain words, what actually expires and what doesn't — every
// broker here uses a permanent API Key/App credential + a TOTP secret that never expires, and
// this backend auto-refreshes the short-lived daily session/access-token on its own (no manual
// re-paste needed), UNLIKE Dhan's Access Token which is a manually-pasted JWT the user must
// refresh/replace themselves when it expires (existing Dhan flow, unchanged).
const BROKER_META: Record<BrokerName, { label: string; hint: string; renewal: string }> = {
  DHAN: {
    label: 'Dhan', hint: 'Default broker — credentials managed on the Settings page',
    renewal: '⚠️ Access Token above expires (see countdown) — click Refresh, or paste a new token from Dhan when it does.',
  },
  // TOTP flow (API Key + TOTP Secret) — this account's current key was generated via Groww's own
  // "TOTP token" screen (see growwTokenService.ts for the full investigation, including an
  // earlier key on this same account that needed Checksum/Approval instead).
  GROWW: {
    label: 'Groww', hint: 'API Key + TOTP Secret from Groww\'s "Generate TOTP token" screen',
    renewal: '✅ API Key + TOTP Secret never expire — enter once. The daily access token (resets 6 AM IST) is fetched automatically, no action needed.',
  },
  ANGEL_ONE: {
    label: 'Angel One', hint: 'API Key, Client Code, PIN + TOTP Secret from SmartAPI developer portal',
    renewal: '✅ These credentials never expire — enter once. The daily login session is refreshed automatically, no action needed.',
  },
  FYERS: {
    label: 'FYERS', hint: 'App ID, Secret Key + Redirect URI from your FYERS API app',
    renewal: '⚠️ FYERS requires a REAL interactive login — click "Login with FYERS" below once, and again any time the session goes stale. FYERS\'s own headless TOTP automation started rejecting requests live, so it cannot auto-refresh itself like Groww/Angel One/Kotak Neo.',
  },
  ALICE_BLUE: {
    label: 'Alice Blue', hint: 'App Code, API Secret + Redirect URI from the A3 Developer Portal (a3.aliceblueonline.com)',
    renewal: '⚠️ Alice Blue requires a REAL interactive login — click "Login with Alice Blue" below once, and again any time the session goes stale. It cannot auto-refresh itself like the other brokers.',
  },
  KOTAK_NEO: {
    label: 'Kotak Neo', hint: 'UCC, Mobile Number, MPIN, TOTP Secret + Consumer Key from the Trade API page (More → Trade API) in the Kotak Neo app/website',
    renewal: '✅ These credentials never expire — enter once. The daily trade session is refreshed automatically via TOTP login, no action needed.',
  },
  UPSTOX: {
    label: 'Upstox', hint: 'API Key, API Secret + Redirect URI from your Upstox Developer app (developer.upstox.com)',
    renewal: '⚠️ Upstox requires a REAL interactive login — click "Login with Upstox" below once, and again any time the session goes stale. It cannot auto-refresh itself like Groww/Angel One/Kotak Neo.',
  },
  ZERODHA: {
    label: 'Zerodha', hint: 'API Key, API Secret + Redirect URL from your Kite Connect app (developers.kite.trade)',
    renewal: '⚠️ Zerodha sessions always expire 6 AM IST and cannot be auto-renewed (automating Kite\'s login is against exchange regulations) — click "Login with Zerodha" below every trading day.',
  },
};

const inputCss: React.CSSProperties = {
  fontSize: '12px', padding: '7px 10px', borderRadius: '7px',
  border: '1px solid #e5e7eb', background: '#f9fafb', outline: 'none',
  width: '100%', transition: 'border-color 0.2s',
};

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
    {children}
  </div>
);

const MsgBox = ({ msg }: { msg: { type: string; text: string } | null }) => !msg ? null : (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', borderRadius: '8px', marginBottom: '10px', fontSize: '12px',
    background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
    border: `1px solid ${msg.type === 'success' ? '#86efac' : '#fca5a5'}`,
    color: msg.type === 'success' ? '#166534' : '#991b1b',
  }}>
    {msg.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
    {msg.text}
  </div>
);

const ToggleSwitch = ({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) => (
  <button
    onClick={onChange}
    disabled={disabled}
    style={{
      width: '38px', height: '21px', borderRadius: '999px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background 0.15s', opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      width: '17px', height: '17px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px',
      left: checked ? '19px' : '2px', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    }} />
  </button>
);

// One independent switch per feature, not one switch for the whole broker — the client's
// explicit ask: e.g. Groww enabled for Cash Market while still off for Spread, without touching
// Dhan's own per-feature switches at all.
const FeatureToggleGrid: React.FC<{
  broker: BrokerName;
  toggles: BrokerFeatureToggle[];
  onToggle: (feature: BrokerFeature, current: boolean) => void;
}> = ({ broker, toggles, onToggle }) => {
  const getEnabled = (feature: BrokerFeature) => toggles.find(t => t.broker === broker && t.feature === feature)?.is_enabled ?? (broker === 'DHAN');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginTop: '8px' }}>
      {ALL_BROKER_FEATURES.map(feature => {
        const enabled = getEnabled(feature);
        return (
          <div key={feature} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: enabled ? '#111827' : '#9ca3af' }}>{FEATURE_LABELS[feature]}</span>
            <ToggleSwitch checked={enabled} onChange={() => onToggle(feature, enabled)} />
          </div>
        );
      })}
    </div>
  );
};

const CAPITAL_UNIT_MULTIPLIER: Record<string, number> = { Thousand: 1000, Lakh: 100000 };

// Manually configured Capital Amount per broker — Nifty/Sensex Buying's % Cap field is a
// percentage of THIS (not the live balance), so a multi-strike split always lands on a known,
// predictable total. The Thousand/Lakh dropdown is purely a typing convenience (e.g. "2" +
// "Lakh" = ₹200000) — only the final rupee amount is persisted.
const CapitalAmountField: React.FC<{
  broker: BrokerName;
  capitalAmounts: BrokerCapital[];
  onSaved: (broker: BrokerName, amount: number) => void;
}> = ({ broker, capitalAmounts, onSaved }) => {
  const saved = capitalAmounts.find(c => c.broker === broker)?.capital_amount ?? 0;
  const [num, setNum] = useState('');
  const [unit, setUnit] = useState<'Thousand' | 'Lakh'>('Lakh');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const n = parseFloat(num);
    if (!n || n <= 0) { setError('Enter a positive number'); return; }
    const amount = n * CAPITAL_UNIT_MULTIPLIER[unit];
    setSaving(true); setError('');
    try {
      await brokerAPI.setCapital(broker, amount);
      onSaved(broker, amount);
      setNum('');
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: '8px', padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
      <FieldLabel>Capital Amount (Nifty/Sensex Buying % Cap)</FieldLabel>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
        ₹{saved.toLocaleString('en-IN')}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          type="number" placeholder="e.g. 2" value={num} onChange={e => setNum(e.target.value)}
          style={{ ...inputCss, width: '70px' }}
        />
        <select value={unit} onChange={e => setUnit(e.target.value as 'Thousand' | 'Lakh')} style={{ ...inputCss, width: '95px' }}>
          <option value="Thousand">Thousand</option>
          <option value="Lakh">Lakh</option>
        </select>
        <button
          onClick={handleSave} disabled={saving || !num}
          style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '7px', border: 'none', background: '#2563eb', color: 'white', cursor: saving ? 'default' : 'pointer' }}
        >
          {saving ? '...' : 'Set'}
        </button>
      </div>
      {error && <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '4px' }}>{error}</div>}
    </div>
  );
};

// Dhan's own Order Account card (credential fields) and the standalone Data Feed card below
// share this exact layout — extracted once instead of duplicated, since they're identical apart
// from which `state`/handlers they're bound to.
const DhanAccountCard: React.FC<{
  title: string; state: AccountTokenState;
  onChangeToken: (v: string) => void; onToggleShow: () => void;
  onRefresh: () => void; onSave: () => void;
}> = ({ title, state, onChangeToken, onToggleShow, onRefresh, onSave }) => {
  const expiryColor = getExpiryColor(state.expiry);
  return (
    <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>{title}</div>
      <MsgBox msg={state.msg} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '7px 10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock size={12} color={expiryColor} />
          <span style={{ fontSize: '11px', fontWeight: '600', color: expiryColor }}>
            {state.expiry.isExpired ? 'Token Expired' : `Valid ${state.expiry.hours}h ${state.expiry.minutes}m`}
          </span>
        </div>
      </div>
      <div style={{ marginBottom: '6px' }}>
        <FieldLabel>Client ID</FieldLabel>
        <input value={state.clientId || 'Not set in database'} disabled
          style={{ ...inputCss, background: '#f3f4f6', color: state.clientId ? '#374151' : '#9ca3af', cursor: 'not-allowed' }} />
      </div>
      <div style={{ marginBottom: '8px' }}>
        <FieldLabel>Access Token</FieldLabel>
        <div style={{ position: 'relative' }}>
          <textarea
            rows={2}
            value={state.accessToken}
            onChange={(e) => onChangeToken(e.target.value)}
            placeholder="Paste your Dhan JWT access token here..."
            style={{
              ...inputCss, resize: 'none', fontFamily: 'monospace', fontSize: '10px',
              paddingRight: '30px', lineHeight: '1.4',
              WebkitTextSecurity: state.showToken ? 'none' : 'disc',
            } as React.CSSProperties}
          />
          <button type="button" onClick={onToggleShow}
            style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
            {state.showToken ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={onRefresh} disabled={state.refreshing}
          style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: '600', padding: '6px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', opacity: state.refreshing ? 0.6 : 1 }}>
          <RefreshCw size={11} />
          {state.refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
        <button onClick={onSave} disabled={state.saving}
          style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: '600', padding: '6px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: state.saving ? 0.6 : 1 }}>
          <Save size={11} />
          {state.saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// Dhan gives separate accounts for order placement vs the Data API — moved here verbatim from
// SettingsPage.tsx (which used to own this section) so every broker's credentials live on this
// one screen; the /token API endpoints are unchanged.
interface AccountTokenState {
  clientId: string;
  accessToken: string;
  showToken: boolean;
  saving: boolean;
  refreshing: boolean;
  expiry: { hours: number; minutes: number; isExpired: boolean };
  msg: { type: 'success' | 'error'; text: string } | null;
}
const emptyAccountState = (): AccountTokenState => ({
  clientId: '', accessToken: '', showToken: false, saving: false, refreshing: false,
  expiry: { hours: 0, minutes: 0, isExpired: false }, msg: null,
});
type DhanAccountType = 'ORDER' | 'DATA';

const getExpiryColor = (expiry: { isExpired: boolean; hours: number }) =>
  expiry.isExpired ? '#ef4444' : expiry.hours < 2 ? '#f59e0b' : '#10b981';

const BrokerManagementPage: React.FC = () => {
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const [capitalAmounts, setCapitalAmounts] = useState<BrokerCapital[]>([]);
  const [loading, setLoading] = useState(true);
  const [growwForm, setGrowwForm] = useState({ api_key: '', totp_secret: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [angelForm, setAngelForm] = useState({ api_key: '', client_code: '', pin: '', totp_secret: '' });
  const [showAngelPin, setShowAngelPin] = useState(false);
  const [showAngelSecret, setShowAngelSecret] = useState(false);
  const [angelSaving, setAngelSaving] = useState(false);
  const [angelTesting, setAngelTesting] = useState(false);
  const [fyersForm, setFyersForm] = useState({ client_id: '', secret_key: '', redirect_uri: 'https://api.tradingmyappdev.net.in/api/fyers/callback' });
  const [showFyersSecretKey, setShowFyersSecretKey] = useState(false);
  const [fyersSaving, setFyersSaving] = useState(false);
  const [fyersTesting, setFyersTesting] = useState(false);
  const [fyersLoggingIn, setFyersLoggingIn] = useState(false);
  const [fyersConnected, setFyersConnected] = useState(false);
  const [fyersSessionDate, setFyersSessionDate] = useState<string | null>(null);
  const [kotakForm, setKotakForm] = useState({ ucc: '', mobile_number: '', mpin: '', totp_secret: '', consumer_key: '' });
  const [showKotakMpin, setShowKotakMpin] = useState(false);
  const [showKotakTotp, setShowKotakTotp] = useState(false);
  const [showKotakConsumerKey, setShowKotakConsumerKey] = useState(false);
  const [kotakSaving, setKotakSaving] = useState(false);
  const [kotakTesting, setKotakTesting] = useState(false);
  const [aliceForm, setAliceForm] = useState({ app_code: '', api_secret: '', redirect_uri: 'https://api.tradingmyappdev.net.in/api/alice-blue/callback' });
  const [showAliceApiSecret, setShowAliceApiSecret] = useState(false);
  const [aliceSaving, setAliceSaving] = useState(false);
  const [aliceTesting, setAliceTesting] = useState(false);
  const [aliceLoggingIn, setAliceLoggingIn] = useState(false);
  const [aliceConnected, setAliceConnected] = useState(false);
  const [aliceSessionDate, setAliceSessionDate] = useState<string | null>(null);
  const [upstoxForm, setUpstoxForm] = useState({ api_key: '', api_secret: '', redirect_uri: 'https://api.tradingmyappdev.net.in/api/upstox/callback' });
  const [showUpstoxApiSecret, setShowUpstoxApiSecret] = useState(false);
  const [upstoxSaving, setUpstoxSaving] = useState(false);
  const [upstoxTesting, setUpstoxTesting] = useState(false);
  const [upstoxLoggingIn, setUpstoxLoggingIn] = useState(false);
  const [upstoxConnected, setUpstoxConnected] = useState(false);
  const [upstoxSessionDate, setUpstoxSessionDate] = useState<string | null>(null);
  const [zerodhaForm, setZerodhaForm] = useState({ api_key: '', api_secret: '', redirect_uri: 'https://api.tradingmyappdev.net.in/api/zerodha/callback' });
  const [showZerodhaApiSecret, setShowZerodhaApiSecret] = useState(false);
  const [zerodhaSaving, setZerodhaSaving] = useState(false);
  const [zerodhaTesting, setZerodhaTesting] = useState(false);
  const [zerodhaLoggingIn, setZerodhaLoggingIn] = useState(false);
  const [zerodhaConnected, setZerodhaConnected] = useState(false);
  const [zerodhaSessionDate, setZerodhaSessionDate] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [orderAccount, setOrderAccount] = useState<AccountTokenState>(emptyAccountState());
  const [dataAccount, setDataAccount] = useState<AccountTokenState>(emptyAccountState());
  const dhanAccountSetter = (type: DhanAccountType) => (type === 'ORDER' ? setOrderAccount : setDataAccount);

  const fetchDhanToken = async (type: DhanAccountType) => {
    const setState = dhanAccountSetter(type);
    try {
      const res = await api.get(`/token?type=${type}`);
      setState(prev => ({
        ...prev,
        clientId: res.data.client_id || prev.clientId,
        accessToken: res.data.access_token || prev.accessToken,
        expiry: { hours: res.data.hours_remaining || 0, minutes: res.data.minutes_remaining || 0, isExpired: res.data.is_expired || false },
      }));
    } catch {}
  };

  useEffect(() => { fetchDhanToken('ORDER'); fetchDhanToken('DATA'); }, []);

  const showDhanAccountMsg = (type: DhanAccountType, msgType: 'success' | 'error', text: string) => {
    const setState = dhanAccountSetter(type);
    setState(prev => ({ ...prev, msg: { type: msgType, text } }));
    setTimeout(() => setState(prev => ({ ...prev, msg: null })), 3000);
  };

  const handleDhanTokenSave = async (type: DhanAccountType) => {
    const state = type === 'ORDER' ? orderAccount : dataAccount;
    const setState = dhanAccountSetter(type);
    if (!state.accessToken.trim()) { showDhanAccountMsg(type, 'error', 'Please enter a valid access token'); return; }
    try {
      setState(prev => ({ ...prev, saving: true }));
      await api.post('/token', { type, access_token: state.accessToken.trim(), client_id: state.clientId.trim() });
      showDhanAccountMsg(type, 'success', 'Token saved successfully');
      await fetchDhanToken(type);
    } catch (err: any) {
      showDhanAccountMsg(type, 'error', err.response?.data?.error || 'Failed to save token');
    } finally { setState(prev => ({ ...prev, saving: false })); }
  };

  const handleDhanTokenRefresh = async (type: DhanAccountType) => {
    const setState = dhanAccountSetter(type);
    try {
      setState(prev => ({ ...prev, refreshing: true }));
      const res = await api.post('/token/refresh', { type });
      setState(prev => ({ ...prev, accessToken: res.data.access_token }));
      showDhanAccountMsg(type, 'success', 'Token refreshed successfully');
      await fetchDhanToken(type);
    } catch (err: any) {
      showDhanAccountMsg(type, 'error', err.response?.data?.error || 'Failed to refresh token');
    } finally { setState(prev => ({ ...prev, refreshing: false })); }
  };

  const fetchBrokers = async () => {
    try {
      const res = await brokerAPI.list();
      setBrokers(res.data);
      const groww = res.data.find(b => b.broker === 'GROWW');
      if (groww?.credentials?.api_key) {
        setGrowwForm(prev => ({ ...prev, api_key: groww.credentials.api_key || '' }));
      }
      const angel = res.data.find(b => b.broker === 'ANGEL_ONE');
      if (angel?.credentials) {
        setAngelForm(prev => ({
          ...prev,
          api_key: (angel.credentials as any).api_key || '',
          client_code: (angel.credentials as any).client_code || '',
        }));
      }
      const fyers = res.data.find(b => b.broker === 'FYERS');
      if (fyers?.credentials) {
        setFyersForm(prev => ({
          ...prev,
          client_id: (fyers.credentials as any).client_id || '',
          redirect_uri: (fyers.credentials as any).redirect_uri || prev.redirect_uri,
        }));
        setFyersConnected(!!(fyers.credentials as any).connected);
        setFyersSessionDate((fyers.credentials as any).sessionIssuedDate || null);
      }
      const alice = res.data.find(b => b.broker === 'ALICE_BLUE');
      if (alice?.credentials) {
        setAliceForm(prev => ({
          ...prev,
          app_code: (alice.credentials as any).app_code || '',
          redirect_uri: (alice.credentials as any).redirect_uri || prev.redirect_uri,
        }));
        setAliceConnected(!!(alice.credentials as any).connected);
        setAliceSessionDate((alice.credentials as any).sessionIssuedDate || null);
      }
      const kotak = res.data.find(b => b.broker === 'KOTAK_NEO');
      if (kotak?.credentials) {
        setKotakForm(prev => ({
          ...prev,
          ucc: (kotak.credentials as any).ucc || '',
          mobile_number: (kotak.credentials as any).mobile_number || '',
        }));
      }
      const upstox = res.data.find(b => b.broker === 'UPSTOX');
      if (upstox?.credentials) {
        setUpstoxForm(prev => ({
          ...prev,
          api_key: (upstox.credentials as any).api_key || '',
          redirect_uri: (upstox.credentials as any).redirect_uri || prev.redirect_uri,
        }));
        setUpstoxConnected(!!(upstox.credentials as any).connected);
        setUpstoxSessionDate((upstox.credentials as any).sessionIssuedDate || null);
      }
      const zerodha = res.data.find(b => b.broker === 'ZERODHA');
      if (zerodha?.credentials) {
        setZerodhaForm(prev => ({
          ...prev,
          api_key: (zerodha.credentials as any).api_key || '',
          redirect_uri: (zerodha.credentials as any).redirect_uri || prev.redirect_uri,
        }));
        setZerodhaConnected(!!(zerodha.credentials as any).connected);
        setZerodhaSessionDate((zerodha.credentials as any).sessionIssuedDate || null);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const res = await brokerAPI.listFeatures();
      setFeatureToggles(res.data);
    } catch {}
  };

  const fetchCapitalAmounts = async () => {
    try {
      const res = await brokerAPI.listCapital();
      setCapitalAmounts(res.data);
    } catch {}
  };

  const handleCapitalSaved = (broker: BrokerName, amount: number) => {
    setCapitalAmounts(prev => {
      const next = prev.filter(c => c.broker !== broker);
      next.push({ broker, capital_amount: amount });
      return next;
    });
  };

  useEffect(() => { fetchBrokers(); fetchFeatureToggles(); fetchCapitalAmounts(); }, []);

  // Optimistic toggle → persist → rollback on failure, same pattern as the per-direction enable
  // switch already used elsewhere (SpreadEntryExitPage.tsx's toggleDirectionEnabled).
  const handleFeatureToggle = async (broker: BrokerName, feature: BrokerFeature, current: boolean) => {
    setFeatureToggles(prev => {
      const exists = prev.some(t => t.broker === broker && t.feature === feature);
      return exists
        ? prev.map(t => (t.broker === broker && t.feature === feature) ? { ...t, is_enabled: !current } : t)
        : [...prev, { broker, feature, is_enabled: !current }];
    });
    try {
      await brokerAPI.setFeatureEnabled(broker, feature, !current);
    } catch (err: any) {
      setFeatureToggles(prev => prev.map(t => (t.broker === broker && t.feature === feature) ? { ...t, is_enabled: current } : t));
      showMsg('error', err.response?.data?.error || `Failed to update ${FEATURE_LABELS[feature]} for ${BROKER_META[broker].label}`);
    }
  };

  // Defensive guard: every caller passes `err.response?.data?.error || '...'`, but axios responses
  // are typed `any` — if a broker's backend ever returns a non-string `error` (an object, like the
  // live Groww bug that crashed this whole page with React error #31 by rendering
  // {errorCode, errorMessage} directly as JSX), coerce it here instead of letting it reach render.
  const showMsg = (type: 'success' | 'error', text: string) => {
    const safeText = typeof text === 'string' ? text : JSON.stringify(text);
    setMsg({ type, text: safeText });
    setTimeout(() => setMsg(null), 3500);
  };

  const handleGrowwSave = async () => {
    if (!growwForm.api_key.trim() || !growwForm.totp_secret.trim()) {
      showMsg('error', 'API Key and TOTP Secret are both required');
      return;
    }
    try {
      setSaving(true);
      await brokerAPI.saveCredentials('GROWW', growwForm);
      showMsg('success', 'Groww credentials saved');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Groww credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await brokerAPI.testConnection('GROWW');
      if (res.data.success) showMsg('success', 'Connected to Groww successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleAngelSave = async () => {
    if (!angelForm.api_key.trim() || !angelForm.client_code.trim() || !angelForm.pin.trim() || !angelForm.totp_secret.trim()) {
      showMsg('error', 'API Key, Client Code, PIN and TOTP Secret are all required');
      return;
    }
    try {
      setAngelSaving(true);
      await brokerAPI.saveCredentials('ANGEL_ONE', angelForm as any);
      showMsg('success', 'Angel One credentials saved');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Angel One credentials');
    } finally {
      setAngelSaving(false);
    }
  };

  const handleAngelTest = async () => {
    try {
      setAngelTesting(true);
      const res = await brokerAPI.testConnection('ANGEL_ONE');
      if (res.data.success) showMsg('success', 'Connected to Angel One successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setAngelTesting(false);
    }
  };

  const handleFyersSave = async () => {
    const { client_id, secret_key, redirect_uri } = fyersForm;
    if (!client_id.trim() || !secret_key.trim() || !redirect_uri.trim()) {
      showMsg('error', 'App ID, Secret Key and Redirect URI are all required');
      return;
    }
    try {
      setFyersSaving(true);
      await brokerAPI.saveCredentials('FYERS', fyersForm as any);
      showMsg('success', 'FYERS credentials saved — now click "Login with FYERS"');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save FYERS credentials');
    } finally {
      setFyersSaving(false);
    }
  };

  // FYERS's OAuth app requires a REAL interactive browser login (same reasoning as Upstox/Alice
  // Blue/Zerodha above — see fyersTokenService.ts for why the previous headless TOTP-simulated
  // login stopped working) — opens a popup to FYERS's own login page; after the user logs in
  // there, FYERS redirects to our backend's /api/fyers/callback, which finishes the token
  // exchange server-side. We just poll fetchBrokers afterwards to pick up the resulting
  // "connected" status.
  const handleFyersLogin = async () => {
    try {
      setFyersLoggingIn(true);
      const res = await brokerAPI.getFyersLoginUrl();
      const popup = window.open(res.data.loginUrl, '_blank', 'width=500,height=650');
      showMsg('success', 'Log in to FYERS in the new tab, then come back here and click Test Connection');
      const poll = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(poll);
          setFyersLoggingIn(false);
          await fetchBrokers();
        }
      }, 1500);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to start FYERS login');
      setFyersLoggingIn(false);
    }
  };

  const handleFyersTest = async () => {
    try {
      setFyersTesting(true);
      const res = await brokerAPI.testConnection('FYERS');
      if (res.data.success) showMsg('success', 'Connected to FYERS successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setFyersTesting(false);
    }
  };

  const handleAliceSave = async () => {
    if (!aliceForm.app_code.trim() || !aliceForm.api_secret.trim() || !aliceForm.redirect_uri.trim()) {
      showMsg('error', 'App Code, API Secret and Redirect URI are all required');
      return;
    }
    try {
      setAliceSaving(true);
      await brokerAPI.saveCredentials('ALICE_BLUE', aliceForm as any);
      showMsg('success', 'Alice Blue credentials saved — now click "Login with Alice Blue"');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Alice Blue credentials');
    } finally {
      setAliceSaving(false);
    }
  };

  // Alice Blue's A3 app requires a REAL interactive browser login (no TOTP/PIN headless path) —
  // opens a popup to Alice Blue's own login page; after the user logs in there, Alice Blue
  // redirects to our backend's /api/alice-blue/callback, which finishes the exchange server-side.
  // We just poll fetchBrokers afterwards to pick up the resulting "connected" status.
  const handleAliceLogin = async () => {
    try {
      setAliceLoggingIn(true);
      const res = await brokerAPI.getAliceBlueLoginUrl();
      const popup = window.open(res.data.loginUrl, '_blank', 'width=500,height=650');
      showMsg('success', 'Log in to Alice Blue in the new tab, then come back here and click Test Connection');
      const poll = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(poll);
          setAliceLoggingIn(false);
          await fetchBrokers();
        }
      }, 1500);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to start Alice Blue login');
      setAliceLoggingIn(false);
    }
  };

  const handleAliceTest = async () => {
    try {
      setAliceTesting(true);
      const res = await brokerAPI.testConnection('ALICE_BLUE');
      if (res.data.success) showMsg('success', 'Connected to Alice Blue successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setAliceTesting(false);
    }
  };

  const handleKotakSave = async () => {
    const { ucc, mobile_number, mpin, totp_secret, consumer_key } = kotakForm;
    if (!ucc.trim() || !mobile_number.trim() || !mpin.trim() || !totp_secret.trim() || !consumer_key.trim()) {
      showMsg('error', 'UCC, Mobile Number, MPIN, TOTP Secret and Consumer Key are all required');
      return;
    }
    try {
      setKotakSaving(true);
      await brokerAPI.saveCredentials('KOTAK_NEO', kotakForm as any);
      showMsg('success', 'Kotak Neo credentials saved');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Kotak Neo credentials');
    } finally {
      setKotakSaving(false);
    }
  };

  const handleKotakTest = async () => {
    try {
      setKotakTesting(true);
      const res = await brokerAPI.testConnection('KOTAK_NEO');
      if (res.data.success) showMsg('success', 'Connected to Kotak Neo successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setKotakTesting(false);
    }
  };

  const handleUpstoxSave = async () => {
    if (!upstoxForm.api_key.trim() || !upstoxForm.api_secret.trim() || !upstoxForm.redirect_uri.trim()) {
      showMsg('error', 'API Key, API Secret and Redirect URI are all required');
      return;
    }
    try {
      setUpstoxSaving(true);
      await brokerAPI.saveCredentials('UPSTOX', upstoxForm as any);
      showMsg('success', 'Upstox credentials saved — now click "Login with Upstox"');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Upstox credentials');
    } finally {
      setUpstoxSaving(false);
    }
  };

  // Upstox's OAuth app requires a REAL interactive browser login (no TOTP/PIN headless path,
  // same reasoning as Alice Blue above) — opens a popup to Upstox's own login page; after the
  // user logs in there, Upstox redirects to our backend's /api/upstox/callback, which finishes
  // the token exchange server-side. We just poll fetchBrokers afterwards to pick up the
  // resulting "connected" status.
  const handleUpstoxLogin = async () => {
    try {
      setUpstoxLoggingIn(true);
      const res = await brokerAPI.getUpstoxLoginUrl();
      const popup = window.open(res.data.loginUrl, '_blank', 'width=500,height=650');
      showMsg('success', 'Log in to Upstox in the new tab, then come back here and click Test Connection');
      const poll = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(poll);
          setUpstoxLoggingIn(false);
          await fetchBrokers();
        }
      }, 1500);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to start Upstox login');
      setUpstoxLoggingIn(false);
    }
  };

  const handleUpstoxTest = async () => {
    try {
      setUpstoxTesting(true);
      const res = await brokerAPI.testConnection('UPSTOX');
      if (res.data.success) showMsg('success', 'Connected to Upstox successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setUpstoxTesting(false);
    }
  };

  const handleZerodhaSave = async () => {
    if (!zerodhaForm.api_key.trim() || !zerodhaForm.api_secret.trim() || !zerodhaForm.redirect_uri.trim()) {
      showMsg('error', 'API Key, API Secret and Redirect URL are all required');
      return;
    }
    try {
      setZerodhaSaving(true);
      await brokerAPI.saveCredentials('ZERODHA', zerodhaForm as any);
      showMsg('success', 'Zerodha credentials saved — now click "Login with Zerodha"');
      await fetchBrokers();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to save Zerodha credentials');
    } finally {
      setZerodhaSaving(false);
    }
  };

  // Zerodha's Kite Connect app also requires a REAL interactive browser login — and unlike every
  // other broker here, it CANNOT be auto-refreshed at all (Zerodha's own access tokens always
  // expire 6 AM IST, and automating the login step is against exchange regulations per Kite
  // Connect's own forum guidance), so this popup needs to be re-opened every trading day.
  const handleZerodhaLogin = async () => {
    try {
      setZerodhaLoggingIn(true);
      const res = await brokerAPI.getZerodhaLoginUrl();
      const popup = window.open(res.data.loginUrl, '_blank', 'width=500,height=650');
      showMsg('success', 'Log in to Zerodha in the new tab, then come back here and click Test Connection');
      const poll = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(poll);
          setZerodhaLoggingIn(false);
          await fetchBrokers();
        }
      }, 1500);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Failed to start Zerodha login');
      setZerodhaLoggingIn(false);
    }
  };

  const handleZerodhaTest = async () => {
    try {
      setZerodhaTesting(true);
      const res = await brokerAPI.testConnection('ZERODHA');
      if (res.data.success) showMsg('success', 'Connected to Zerodha successfully');
      else showMsg('error', res.data.error || 'Connection failed');
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Connection test failed');
    } finally {
      setZerodhaTesting(false);
    }
  };

  const groww = brokers.find(b => b.broker === 'GROWW');
  const angelOne = brokers.find(b => b.broker === 'ANGEL_ONE');
  const fyers = brokers.find(b => b.broker === 'FYERS');
  const aliceBlue = brokers.find(b => b.broker === 'ALICE_BLUE');
  const kotakNeo = brokers.find(b => b.broker === 'KOTAK_NEO');
  const upstox = brokers.find(b => b.broker === 'UPSTOX');
  const zerodha = brokers.find(b => b.broker === 'ZERODHA');
  const comingSoon: BrokerName[] = [];

  if (loading) {
    return <MainLayout><div style={{ padding: '24px', fontSize: '13px', color: '#6b7280' }}>Loading brokers...</div></MainLayout>;
  }

  return (
    <MainLayout>
      <div style={{ padding: '20px 24px', background: '#f8fafc', minHeight: 'calc(100vh - 60px)', fontSize: '13px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>Broker Management</div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
            Each config picks one broker to trade through. Each broker has its own switch per feature below —
            e.g. Groww can be on for Cash Market while still off for Spread. Disabling a feature pauses new
            entries for configs mapped to that broker — open positions can still be exited.
          </div>
        </div>

        <MsgBox msg={msg} />

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>

          {/* DHAN — order placement only now; Data Feed moved to its own common card below
              since it's used for live market data regardless of which broker places orders. */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#2563eb" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.DHAN.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Default broker — order account used for placing orders</div>
            <div style={{ fontSize: '10px', color: '#b45309', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.DHAN.renewal}</div>

            <FeatureToggleGrid broker="DHAN" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('DHAN', f, c)} />
            <CapitalAmountField broker="DHAN" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginTop: '14px' }}>
              <DhanAccountCard
                title="Order Account" state={orderAccount}
                onChangeToken={(v) => setOrderAccount(prev => ({ ...prev, accessToken: v }))}
                onToggleShow={() => setOrderAccount(prev => ({ ...prev, showToken: !prev.showToken }))}
                onRefresh={() => handleDhanTokenRefresh('ORDER')}
                onSave={() => handleDhanTokenSave('ORDER')}
              />
            </div>
          </div>

          {/* GROWW */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Wifi size={16} color="#10b981" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.GROWW.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.GROWW.hint}</div>
            <div style={{ fontSize: '10px', color: '#059669', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.GROWW.renewal}</div>

            <FeatureToggleGrid broker="GROWW" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('GROWW', f, c)} />
            <CapitalAmountField broker="GROWW" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Key</FieldLabel>
              <input
                value={growwForm.api_key}
                onChange={(e) => setGrowwForm(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="From Groww Cloud API Keys page"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>TOTP Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={growwForm.totp_secret}
                  onChange={(e) => setGrowwForm(prev => ({ ...prev, totp_secret: e.target.value }))}
                  placeholder={groww?.credentials?.hasTotpSecret ? '●●●●●●●● (saved)' : 'From Groww\'s "Generate TOTP token" screen'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowSecret(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleGrowwSave} disabled={saving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: saving ? 0.6 : 1 }}>
                <Save size={13} />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleTest} disabled={testing}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: testing ? 0.6 : 1 }}>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* ANGEL ONE */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#dc2626" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.ANGEL_ONE.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.ANGEL_ONE.hint}</div>
            <div style={{ fontSize: '10px', color: '#059669', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.ANGEL_ONE.renewal}</div>

            <FeatureToggleGrid broker="ANGEL_ONE" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('ANGEL_ONE', f, c)} />
            <CapitalAmountField broker="ANGEL_ONE" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Key</FieldLabel>
              <input
                value={angelForm.api_key}
                onChange={(e) => setAngelForm(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="From SmartAPI developer portal"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>Client Code</FieldLabel>
              <input
                value={angelForm.client_code}
                onChange={(e) => setAngelForm(prev => ({ ...prev, client_code: e.target.value }))}
                placeholder="Angel One trading client code"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>PIN</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showAngelPin ? 'text' : 'password'}
                  value={angelForm.pin}
                  onChange={(e) => setAngelForm(prev => ({ ...prev, pin: e.target.value }))}
                  placeholder={angelOne?.credentials?.hasPin ? '●●●● (saved)' : 'Trading PIN / MPIN'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowAngelPin(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showAngelPin ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>TOTP Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showAngelSecret ? 'text' : 'password'}
                  value={angelForm.totp_secret}
                  onChange={(e) => setAngelForm(prev => ({ ...prev, totp_secret: e.target.value }))}
                  placeholder={angelOne?.credentials?.hasToTpSecret ? '●●●●●●●● (saved)' : 'TOTP secret / QR code value'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowAngelSecret(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showAngelSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleAngelSave} disabled={angelSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: angelSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {angelSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleAngelTest} disabled={angelTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: angelTesting ? 0.6 : 1 }}>
                {angelTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* FYERS */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#f97316" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.FYERS.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.FYERS.hint}</div>
            <div style={{ fontSize: '10px', color: '#b45309', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.FYERS.renewal}</div>

            <FeatureToggleGrid broker="FYERS" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('FYERS', f, c)} />
            <CapitalAmountField broker="FYERS" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>App ID</FieldLabel>
              <input
                value={fyersForm.client_id}
                onChange={(e) => setFyersForm(prev => ({ ...prev, client_id: e.target.value }))}
                placeholder="e.g. ABCD1234EF-100, from your FYERS API app"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Secret Key</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showFyersSecretKey ? 'text' : 'password'}
                  value={fyersForm.secret_key}
                  onChange={(e) => setFyersForm(prev => ({ ...prev, secret_key: e.target.value }))}
                  placeholder={fyers?.credentials?.hasSecretKey ? '●●●●●●●● (saved)' : 'App Secret Key'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowFyersSecretKey(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showFyersSecretKey ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Redirect URI</FieldLabel>
              <input
                value={fyersForm.redirect_uri}
                onChange={(e) => setFyersForm(prev => ({ ...prev, redirect_uri: e.target.value }))}
                placeholder="Must match your FYERS app's registered redirect URI"
                style={inputCss}
              />
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>Must exactly match what you registered when creating the FYERS API app</div>
            </div>

            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: fyersConnected ? '#059669' : '#b45309' }}>
              {fyersConnected ? `✅ Connected${fyersSessionDate ? ` (session from ${fyersSessionDate})` : ''}` : '⚠️ Not logged in yet'}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <button onClick={handleFyersSave} disabled={fyersSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: fyersSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {fyersSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleFyersTest} disabled={fyersTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: fyersTesting ? 0.6 : 1 }}>
                {fyersTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
            <button onClick={handleFyersLogin} disabled={fyersLoggingIn}
              style={{ width: '100%', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white', opacity: fyersLoggingIn ? 0.6 : 1 }}>
              {fyersLoggingIn ? 'Waiting for login...' : 'Login with FYERS'}
            </button>
          </div>

          {/* ALICE BLUE */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#7c3aed" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.ALICE_BLUE.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.ALICE_BLUE.hint}</div>
            <div style={{ fontSize: '10px', color: '#059669', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.ALICE_BLUE.renewal}</div>

            <FeatureToggleGrid broker="ALICE_BLUE" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('ALICE_BLUE', f, c)} />
            <CapitalAmountField broker="ALICE_BLUE" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>App Code</FieldLabel>
              <input
                value={aliceForm.app_code}
                onChange={(e) => setAliceForm(prev => ({ ...prev, app_code: e.target.value }))}
                placeholder="From a3.aliceblueonline.com → My Apps"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showAliceApiSecret ? 'text' : 'password'}
                  value={aliceForm.api_secret}
                  onChange={(e) => setAliceForm(prev => ({ ...prev, api_secret: e.target.value }))}
                  placeholder={aliceBlue?.credentials?.hasApiSecret ? '●●●●●●●● (saved)' : 'From a3.aliceblueonline.com → My Apps'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowAliceApiSecret(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showAliceApiSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Redirect URI</FieldLabel>
              <input
                value={aliceForm.redirect_uri}
                onChange={(e) => setAliceForm(prev => ({ ...prev, redirect_uri: e.target.value }))}
                placeholder="Must exactly match what you registered in the A3 app"
                style={inputCss}
              />
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>Must exactly match what you registered when creating the Alice Blue A3 app</div>
            </div>

            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: aliceConnected ? '#059669' : '#b45309' }}>
              {aliceConnected ? `✅ Connected${aliceSessionDate ? ` (session from ${aliceSessionDate})` : ''}` : '⚠️ Not logged in yet'}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <button onClick={handleAliceSave} disabled={aliceSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: aliceSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {aliceSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleAliceTest} disabled={aliceTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: aliceTesting ? 0.6 : 1 }}>
                {aliceTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
            <button onClick={handleAliceLogin} disabled={aliceLoggingIn}
              style={{ width: '100%', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white', opacity: aliceLoggingIn ? 0.6 : 1 }}>
              {aliceLoggingIn ? 'Waiting for login...' : 'Login with Alice Blue'}
            </button>
          </div>

          {/* KOTAK NEO */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#dc2626" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.KOTAK_NEO.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.KOTAK_NEO.hint}</div>
            <div style={{ fontSize: '10px', color: '#059669', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.KOTAK_NEO.renewal}</div>

            <FeatureToggleGrid broker="KOTAK_NEO" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('KOTAK_NEO', f, c)} />
            <CapitalAmountField broker="KOTAK_NEO" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>UCC (Client Code)</FieldLabel>
              <input
                value={kotakForm.ucc}
                onChange={(e) => setKotakForm(prev => ({ ...prev, ucc: e.target.value }))}
                placeholder="e.g. 14CN5"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>Mobile Number</FieldLabel>
              <input
                value={kotakForm.mobile_number}
                onChange={(e) => setKotakForm(prev => ({ ...prev, mobile_number: e.target.value }))}
                placeholder="Registered mobile number"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>MPIN</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showKotakMpin ? 'text' : 'password'}
                  value={kotakForm.mpin}
                  onChange={(e) => setKotakForm(prev => ({ ...prev, mpin: e.target.value }))}
                  placeholder={kotakNeo?.credentials?.hasMpin ? '●●●●●● (saved)' : 'Trading MPIN'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowKotakMpin(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showKotakMpin ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>TOTP Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showKotakTotp ? 'text' : 'password'}
                  value={kotakForm.totp_secret}
                  onChange={(e) => setKotakForm(prev => ({ ...prev, totp_secret: e.target.value }))}
                  placeholder={kotakNeo?.credentials?.hasToTpSecret ? '●●●●●●●● (saved)' : 'TOTP secret / QR code value'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowKotakTotp(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showKotakTotp ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Consumer Key</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showKotakConsumerKey ? 'text' : 'password'}
                  value={kotakForm.consumer_key}
                  onChange={(e) => setKotakForm(prev => ({ ...prev, consumer_key: e.target.value }))}
                  placeholder={kotakNeo?.credentials?.hasConsumerKey ? '●●●●●●●● (saved)' : 'From More → Trade API'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowKotakConsumerKey(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showKotakConsumerKey ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>Also make sure the Trade API app's IP Whitelist matches this server's IP</div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleKotakSave} disabled={kotakSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: kotakSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {kotakSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleKotakTest} disabled={kotakTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: kotakTesting ? 0.6 : 1 }}>
                {kotakTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* UPSTOX */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#6b7280" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.UPSTOX.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.UPSTOX.hint}</div>
            <div style={{ fontSize: '10px', color: '#b45309', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.UPSTOX.renewal}</div>

            <FeatureToggleGrid broker="UPSTOX" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('UPSTOX', f, c)} />
            <CapitalAmountField broker="UPSTOX" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Key</FieldLabel>
              <input
                value={upstoxForm.api_key}
                onChange={(e) => setUpstoxForm(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="From developer.upstox.com → Apps"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showUpstoxApiSecret ? 'text' : 'password'}
                  value={upstoxForm.api_secret}
                  onChange={(e) => setUpstoxForm(prev => ({ ...prev, api_secret: e.target.value }))}
                  placeholder={upstox?.credentials?.hasApiSecret ? '●●●●●●●● (saved)' : 'From developer.upstox.com → Apps'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowUpstoxApiSecret(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showUpstoxApiSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Redirect URI</FieldLabel>
              <input
                value={upstoxForm.redirect_uri}
                onChange={(e) => setUpstoxForm(prev => ({ ...prev, redirect_uri: e.target.value }))}
                placeholder="Must exactly match what you registered in the Upstox app"
                style={inputCss}
              />
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>Must exactly match what you registered when creating the Upstox app</div>
            </div>

            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: upstoxConnected ? '#059669' : '#b45309' }}>
              {upstoxConnected ? `✅ Connected${upstoxSessionDate ? ` (session from ${upstoxSessionDate})` : ''}` : '⚠️ Not logged in yet'}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <button onClick={handleUpstoxSave} disabled={upstoxSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: upstoxSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {upstoxSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleUpstoxTest} disabled={upstoxTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: upstoxTesting ? 0.6 : 1 }}>
                {upstoxTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
            <button onClick={handleUpstoxLogin} disabled={upstoxLoggingIn}
              style={{ width: '100%', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white', opacity: upstoxLoggingIn ? 0.6 : 1 }}>
              {upstoxLoggingIn ? 'Waiting for login...' : 'Login with Upstox'}
            </button>
          </div>

          {/* ZERODHA */}
          <div style={{ flex: '1 1 320px', maxWidth: '380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Shield size={16} color="#387ed1" />
              <span style={{ fontWeight: '700', fontSize: '13px' }}>{BROKER_META.ZERODHA.label}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{BROKER_META.ZERODHA.hint}</div>
            <div style={{ fontSize: '10px', color: '#b45309', marginBottom: '8px', lineHeight: '1.4' }}>{BROKER_META.ZERODHA.renewal}</div>

            <FeatureToggleGrid broker="ZERODHA" toggles={featureToggles} onToggle={(f, c) => handleFeatureToggle('ZERODHA', f, c)} />
            <CapitalAmountField broker="ZERODHA" capitalAmounts={capitalAmounts} onSaved={handleCapitalSaved} />
            <div style={{ marginBottom: '10px' }} />

            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Key</FieldLabel>
              <input
                value={zerodhaForm.api_key}
                onChange={(e) => setZerodhaForm(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="From developers.kite.trade → Apps"
                style={inputCss}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <FieldLabel>API Secret</FieldLabel>
              <div style={{ position: 'relative' }}>
                <input
                  type={showZerodhaApiSecret ? 'text' : 'password'}
                  value={zerodhaForm.api_secret}
                  onChange={(e) => setZerodhaForm(prev => ({ ...prev, api_secret: e.target.value }))}
                  placeholder={zerodha?.credentials?.hasApiSecret ? '●●●●●●●● (saved)' : 'From developers.kite.trade → Apps'}
                  style={{ ...inputCss, paddingRight: '32px' }}
                />
                <button type="button" onClick={() => setShowZerodhaApiSecret(s => !s)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showZerodhaApiSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <FieldLabel>Redirect URL</FieldLabel>
              <input
                value={zerodhaForm.redirect_uri}
                onChange={(e) => setZerodhaForm(prev => ({ ...prev, redirect_uri: e.target.value }))}
                placeholder="Must exactly match the Redirect URL in your Kite Connect app"
                style={inputCss}
              />
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>Must exactly match the Redirect URL registered in your Kite Connect app console</div>
            </div>

            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: zerodhaConnected ? '#059669' : '#b45309' }}>
              {zerodhaConnected ? `✅ Connected${zerodhaSessionDate ? ` (session from ${zerodhaSessionDate})` : ''}` : '⚠️ Not logged in yet'}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <button onClick={handleZerodhaSave} disabled={zerodhaSaving}
                style={{ flex: 1, border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: zerodhaSaving ? 0.6 : 1 }}>
                <Save size={13} />
                {zerodhaSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleZerodhaTest} disabled={zerodhaTesting}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: '#fff', color: '#374151', opacity: zerodhaTesting ? 0.6 : 1 }}>
                {zerodhaTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
            <button onClick={handleZerodhaLogin} disabled={zerodhaLoggingIn}
              style={{ width: '100%', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', padding: '7px 10px', cursor: 'pointer', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white', opacity: zerodhaLoggingIn ? 0.6 : 1 }}>
              {zerodhaLoggingIn ? 'Waiting for login...' : 'Login with Zerodha'}
            </button>
          </div>

          {/* COMING SOON (none currently — all 8 brokers are implemented) */}
          {comingSoon.map((broker) => (
            <div key={broker} style={{ flex: '1 1 320px', maxWidth: '380px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', opacity: 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Lock size={16} color="#9ca3af" />
                <span style={{ fontWeight: '700', fontSize: '13px', color: '#6b7280' }}>{BROKER_META[broker].label}</span>
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{BROKER_META[broker].hint}</div>
            </div>
          ))}
        </div>

        {/* DATA FEED — common/shared, not part of the per-broker order-routing matrix above.
            Live LTP/quotes come from this one feed regardless of which broker a config is
            routed through (even a Groww-routed config still prices off this), so it lives in
            its own card rather than nested under Dhan's order-placement card. */}
        <div style={{ marginTop: '16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', maxWidth: '380px' }}>
          <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '4px' }}>Data Feed</div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '10px' }}>
            Shared market-data source for live LTP/quotes across every broker — not tied to the order-routing switches above.
          </div>
          <DhanAccountCard
            title="Data Feed Account" state={dataAccount}
            onChangeToken={(v) => setDataAccount(prev => ({ ...prev, accessToken: v }))}
            onToggleShow={() => setDataAccount(prev => ({ ...prev, showToken: !prev.showToken }))}
            onRefresh={() => handleDhanTokenRefresh('DATA')}
            onSave={() => handleDhanTokenSave('DATA')}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default BrokerManagementPage;
